import type {
  Address,
  AdminAccount,
  Appointment,
  AppUser,
  Article,
  Company,
  Country,
  FlaggedReview,
  Formula,
  NotificationLogEntry,
  NotificationTemplates,
  Offer,
  PregnancyWeek,
  ReminderSettings,
} from '@domain/entities';
import {
  SEED_FORMULAS,
  seedAddressReviews,
  seedAddresses,
  seedAdmins,
  seedAppUsers,
  seedAppointments,
  seedArticles,
  seedCompanies,
  seedCountries,
  seedNotificationTemplates,
  seedOffers,
  seedPregnancyWeeks,
  seedReminder,
  seedReviews,
} from '../../seed/dataset';

export interface InMemoryAddressReview {
  id: string;
  addressId: string;
  author: string;
  rating: number;
  comment: string;
  createdAt: string;
}

/**
 * Base mémoire unique partagée par tous les repositories in-memory.
 * Remplace un vrai backend en développement ; les données ne sont pas persistées.
 */
export class InMemoryDb {
  companies: Company[] = seedCompanies();
  offers: Offer[] = seedOffers();
  articles: Article[] = seedArticles();
  addresses: Address[] = seedAddresses();
  addressReviews: InMemoryAddressReview[] = seedAddressReviews();
  reviews: FlaggedReview[] = seedReviews();
  users: AppUser[] = seedAppUsers();
  appointments: Appointment[] = seedAppointments(this.users);
  admins: AdminAccount[] = seedAdmins();
  formulas: Formula[] = SEED_FORMULAS.map((f) => ({ ...f }));
  countries: Country[] = seedCountries();
  weeks: PregnancyWeek[] = seedPregnancyWeeks();
  reminder: ReminderSettings = seedReminder();
  templates: NotificationTemplates = seedNotificationTemplates();
  notificationLog: NotificationLogEntry[] = [];

  private sequence = 0;

  nextId(prefix: string): string {
    this.sequence += 1;
    return `${prefix}_${Date.now().toString(36)}${this.sequence}`;
  }
}

/** Latence simulée : reproduit les états de chargement de l'UI. */
export function latency<T>(value: T, ms = 180): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function matches(haystack: string, needle: string): boolean {
  if (!needle.trim()) return true;
  return normalize(haystack).includes(normalize(needle));
}

export const TODAY_LABEL = new Date().toLocaleDateString('fr-FR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});
